import LOADER from '@ott/utility-loader';

import { createReducer } from '@reduxjs/toolkit';

import { register } from './actions';
import { authByPin } from './actions/authByPin';
import { checkCaptcha } from './actions/checkCaptcha';
import { enter } from './actions/enter';
import { exit } from './actions/exit';
import { forgotPassword } from './actions/forgotPassword';
import { getBasicUserInfo } from './actions/getBasicUserInfo';
import { getOAuthInfo } from './actions/getOAuthInfo';
import { getPinCode } from './actions/getPinCode';
import { oAuthEnterSuccess } from './actions/oAuthEnterSuccess';
import { regReset } from './actions/regReset';
import { AuthState } from './types';

const initialState: AuthState = {
  infoStatus: LOADER.initial(),
  enterStatus: LOADER.initial(),
  exitStatus: LOADER.initial(),
  registerStatus: LOADER.initial(),
  forgotStatus: LOADER.initial(),
  oAuthStatus: LOADER.initial(),
  sendPinStatus: LOADER.initial(),
  captchaParams: false,
  data: undefined,
  didExit: false,
  didEnter: false,
};

const authReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(authByPin.pending, (state) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.begin();
      state.enterStatus = LOADER.begin();
    })
    .addCase(authByPin.fulfilled, (state) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.initial();
    })
    .addCase(authByPin.rejected, (state, { payload }) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.initial();
      state.enterStatus = LOADER.error(true);
      if (payload) {
        state.enterStatus.errorData = payload.data;
      }
    })
    .addCase(getBasicUserInfo.pending, (state) => {
      state.exitStatus = LOADER.initial();
      state.enterStatus = LOADER.initial();
      state.infoStatus = LOADER.begin();
    })
    .addCase(getBasicUserInfo.fulfilled, (state, { payload }) => {
      state.infoStatus = LOADER.success();
      state.data = payload;
    })
    .addCase(getBasicUserInfo.rejected, (state, { payload }) => {
      state.infoStatus = LOADER.error(true);
      if (payload) {
        state.infoStatus.errorData = payload.data;
      }
    })
    .addCase(checkCaptcha.fulfilled, (state) => {
      state.captchaParams = false;
    })
    .addCase(enter.pending, (state) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.begin();
      state.enterStatus = LOADER.begin();
    })
    .addCase(enter.fulfilled, (state) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.success();
      state.enterStatus = LOADER.success();
      state.didEnter = true;
      state.didExit = false;
    })
    .addCase(enter.rejected, (state, { payload }) => {
      state.exitStatus = LOADER.initial();
      state.infoStatus = LOADER.initial();
      state.enterStatus = LOADER.error(true);
      if (payload) {
        state.enterStatus.errorData = payload.data;
      }
    })
    .addCase(exit.pending, (state) => {
      state.exitStatus = LOADER.begin();
    })
    .addCase(exit.fulfilled, (state) => {
      state.exitStatus = LOADER.success();
      state.infoStatus = LOADER.initial();
      state.didExit = true;
      state.didEnter = false;
      state.data = undefined;
    })
    .addCase(exit.rejected, (state, { payload }) => {
      state.exitStatus = LOADER.error(true);
      if (payload) {
        state.exitStatus.errorData = payload.data;
      }
    })
    .addCase(forgotPassword.pending, (state) => {
      state.forgotStatus = LOADER.begin();
    })
    .addCase(forgotPassword.fulfilled, (state) => {
      state.forgotStatus = LOADER.success();
    })
    .addCase(forgotPassword.rejected, (state, { payload }) => {
      state.forgotStatus = LOADER.error(true);
      if (payload) {
        state.forgotStatus.errorData = payload.data;
      }
    })
    .addCase(getPinCode.pending, (state) => {
      state.enterStatus = LOADER.initial();
      state.sendPinStatus = LOADER.begin();
    })
    .addCase(getPinCode.fulfilled, (state) => {
      state.sendPinStatus = LOADER.success();
    })
    .addCase(getPinCode.rejected, (state, { payload }) => {
      state.sendPinStatus = LOADER.error(true);
      if (payload) {
        state.sendPinStatus.errorData = payload.data;
      }
    })
    .addCase(getOAuthInfo.pending, (state) => {
      state.oAuthStatus = LOADER.begin();
    })
    .addCase(getOAuthInfo.fulfilled, (state, { payload: { oAuthModal, url } }) => {
      state.oAuthUrl = url;
      state.oAuthStatus = LOADER.success();
      if (oAuthModal) {
        // @ts-ignore
        state.oAuthModal = oAuthModal;
      }
    })
    .addCase(getOAuthInfo.rejected, (state, { payload }) => {
      state.oAuthStatus = LOADER.error(true);
      if (payload) {
        state.oAuthStatus.errorData = payload.data;
      }
    })
    .addCase(oAuthEnterSuccess, (state) => {
      state.oAuthStatus = LOADER.success();
      state.oAuthUrl = undefined;
      state.oAuthModal = undefined;
    })
    .addCase(register.pending, (state) => {
      state.registerStatus = LOADER.begin();
    })
    .addCase(register.fulfilled, (state) => {
      state.registerStatus = LOADER.success();
    })
    .addCase(register.rejected, (state, { payload }) => {
      state.registerStatus = LOADER.error(true);
      if (payload) {
        state.registerStatus.errorData = payload.data;
      }
    })
    .addCase(regReset, (state) => {
      state.infoStatus = initialState.infoStatus;
      state.enterStatus = initialState.enterStatus;
      state.exitStatus = initialState.exitStatus;
      state.registerStatus = initialState.registerStatus;
      state.forgotStatus = initialState.forgotStatus;
      state.oAuthStatus = initialState.oAuthStatus;
      state.sendPinStatus = initialState.sendPinStatus;
      state.captchaParams = initialState.captchaParams;
      state.data = initialState.data;
      state.didExit = initialState.didExit;
      state.didEnter = initialState.didEnter;
    });
});

export default authReducer;
